import { useState } from "react";
import type { CarState, CommandType, WindowId } from "../types";
import { WINDOWS, WINDOW_LABEL } from "../types";
import Btn from "./Btn";
import { Icon, type IconName } from "./Icons";
import { PAINTS, type Paint } from "./CarGraphic";
import { DEMO_MODE } from "../supabaseClient";

type Send = (type: CommandType, target?: WindowId | "all" | null) => void;
type Cat = "access" | "windows" | "climate" | "engine" | "scenes" | "car";

const CATS: { id: Cat; label: string; icon: IconName }[] = [
  { id: "access", label: "Access", icon: "lock" },
  { id: "windows", label: "Windows", icon: "win-down" },
  { id: "climate", label: "Climate", icon: "snow" },
  { id: "engine", label: "Engine", icon: "power" },
  { id: "scenes", label: "Scenes", icon: "shield-check" },
  { id: "car", label: "Car", icon: "boot" },
];

const SCENES: { type: CommandType; label: string; icon: IconName; desc: string }[] = [
  { type: "scene_precool", label: "Pre-cool", icon: "snow", desc: "Start · A/C · vent fronts" },
  { type: "scene_leave", label: "Leaving home", icon: "exit", desc: "Unlock · A/C on" },
  { type: "scene_park", label: "Park & arm", icon: "shield-check", desc: "Lock · windows up · kill" },
];

// Category tab bar + the selected category's controls. Everything fits below the car without scrolling.
export default function Deck({
  s, send, busy, paint, setPaint, onAbort, setDemoInput,
}: { s: CarState; send: Send; busy: CommandType | null; paint: Paint; setPaint: (p: Paint) => void; onAbort: () => void; setDemoInput: (p: Partial<CarState>) => void }) {
  const [cat, setCat] = useState<Cat>("access");
  const [target, setTarget] = useState<WindowId | "all">("all");
  const b = (t: CommandType) => busy === t;

  return (
    <div className="deck">
      <nav className="tabs" aria-label="Control categories">
        {CATS.map((c) => (
          <button key={c.id} className={`tab ${cat === c.id ? "on" : ""}`} onClick={() => setCat(c.id)}>
            <span className="tab-icon"><Icon name={c.icon} size={18} /></span>
            <span className="tab-label">{c.label}</span>
          </button>
        ))}
        <button className="tab abort-tab" onClick={onAbort} aria-label="Abort — drop every relay">
          <span className="abort-cap">ABORT</span>
        </button>
      </nav>

      <section className="pane" key={cat}>
        {cat === "access" && (
          <div className="grid3">
            <Btn label="Lock" icon="lock" active={s.locked} busy={b("lock")} onClick={() => send("lock")} />
            <Btn label="Unlock" icon="unlock" active={!s.locked} busy={b("unlock")} onClick={() => send("unlock")} />
            <Btn label={s.bootOpen ? "Close boot" : "Boot"} icon={s.bootOpen ? "boot-open" : "boot"} active={s.bootOpen} busy={b("boot")} onClick={() => send("boot")} />
          </div>
        )}
        {cat === "access" && (
          <div className="seg doors-seg" title={DEMO_MODE ? "Door sensors (demo: tap to open/close)" : "Door sensors"}>
            <span className="seg-label">Doors</span>
            {WINDOWS.map((d) => (
              <button key={d} className={`seg-item ${s.doors[d] ? "on warn" : ""}`} disabled={!DEMO_MODE}
                onClick={() => setDemoInput({ doors: { ...s.doors, [d]: !s.doors[d] } })}>
                {WINDOW_LABEL[d]}
              </button>
            ))}
          </div>
        )}

        {cat === "windows" && (
          <>
            <div className="seg">
              {(["all", ...WINDOWS] as const).map((w) => (
                <button key={w} className={`seg-item ${target === w ? "on" : ""}`} onClick={() => setTarget(w)}>
                  {w === "all" ? "All" : WINDOW_LABEL[w]}
                </button>
              ))}
            </div>
            <div className="grid3">
              <Btn label="Up" icon="win-up" busy={b("win_up")} onClick={() => send("win_up", target)} />
              <Btn label="Vent" icon="win-vent" busy={b("win_vent")} onClick={() => send("win_vent", target)} />
              <Btn label="Down" icon="win-down" busy={b("win_down")} onClick={() => send("win_down", target)} />
            </div>
          </>
        )}

        {cat === "climate" && (
          <>
            <div className="grid2">
              <Btn label="A/C on" icon="snow" active={s.ac} busy={b("ac_on")} onClick={() => send("ac_on")} />
              <Btn label="A/C off" icon="fan-off" active={!s.ac} busy={b("ac_off")} onClick={() => send("ac_off")} />
            </div>
            <div className="seg">
              {[0, 1, 2, 3, 4].map((n) => (
                <button key={n} className={`seg-item ${s.blower === n ? "on" : ""}`} onClick={() => send(`blower_${n as 0}` as CommandType)}>
                  {n === 0 ? "Off" : n}
                </button>
              ))}
            </div>
          </>
        )}

        {cat === "engine" && (
          <>
            <div className="engine-row">
              <Btn label={s.killArmed ? "Kill armed" : "Arm kill"} icon="shield-off" active={s.killArmed} danger={s.killArmed}
                   busy={b("kill_arm")} onClick={() => send(s.killArmed ? "kill_disarm" : "kill_arm")} />
              <Btn label={s.engineRunning ? "Engine stop" : "Engine start"} icon="power" active={s.engineRunning}
                   danger={!s.engineRunning} busy={b("start")} round
                   onClick={() => send(s.engineRunning ? "stop" : "start")} />
              <Btn label={s.reserveArmed ? "Reserved" : "Reserve"} icon="clock" active={s.reserveArmed}
                   busy={b("reserve_arm")} onClick={() => send("reserve_arm")} />
            </div>
            <p className="hint">Start needs neutral · handbrake · reserved · kill off</p>
          </>
        )}

        {cat === "scenes" && (
          <div className="scene-row">
            {SCENES.map((sc) => (
              <button key={sc.type} className="scene" onClick={() => send(sc.type)}>
                <span className="scene-icon"><Icon name={sc.icon} size={20} /></span>
                <span className="scene-label">{sc.label}</span>
                <span className="scene-desc">{sc.desc}</span>
              </button>
            ))}
          </div>
        )}

        {cat === "car" && (
          <div className="car-pane">
            <div className="sc-specs">
              <span>1197 cc K12M</span><span>82 bhp · 113 Nm</span><span>5‑speed manual</span><span>Petrol</span><span>165/80 R14</span>
            </div>
            <div className="sc-paint" role="radiogroup" aria-label="Paint colour">
              {PAINTS.map((p) => (
                <button key={p.name} type="button" role="radio" aria-checked={p.name === paint.name}
                  className={`swatch ${p.name === paint.name ? "on" : ""}`}
                  style={{ background: `linear-gradient(135deg, ${p.hi}, ${p.base} 55%, ${p.shade})` }}
                  title={p.name} onClick={() => setPaint(p)} />
              ))}
              <span className="sc-paint-name">{paint.name}</span>
            </div>
            <p className="hint credit">3D model: <a href="https://sketchfab.com/3d-models/2022-maruti-suzuki-swift-dzire-95451c00cb2d48778f67d798167e7237" target="_blank" rel="noreferrer">BHP3D</a> · CC BY 4.0</p>
          </div>
        )}
      </section>
    </div>
  );
}
